import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { AuthToken } from '@core/models/token';
import { CookieService } from './cookie.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private static readonly USER_STORAGE_KEY = 'user';

  private userSubject: BehaviorSubject<AuthToken | undefined>;
  public user: Observable<AuthToken | undefined>;
  private activeUserStorage?: Storage;

  constructor(
    private router: Router,
    private http: HttpClient,
    private cookies: CookieService
  ) {
    const storedUser = this.getStoredUser();
    this.userSubject = new BehaviorSubject<AuthToken | undefined>(
      storedUser ? new AuthToken(storedUser) : undefined
    );
    this.user = this.userSubject.asObservable();
  }

  public get userValue(): AuthToken | undefined {
    return this.userSubject.value;
  }

  refresh(): Observable<AuthToken | null> {
    if (this.userValue) {
      return this.http.post<AuthToken>(`${environment.apiUrl}/auth/refresh`, {})
      .pipe(
        tap(user => {
          if (user.language && this.cookies.getCookie('lang') !== user.language) {
            this.cookies.setCookie('lang', user.language, 100 * 365);
            location.reload();
          }
          this.setAuthenticatedUser(user);
        })
      );
    } else {
      return of(null);
    }
  }

  login(email: string, password: string, remember = true): Observable<AuthToken> {
    return this.http.post<AuthToken>(`${environment.apiUrl}/auth/login`, { email, password })
    .pipe(
      tap(user => this.setAuthenticatedUser(user, remember ? localStorage : sessionStorage))
    );
  }

  impersonate(id: number): Observable<AuthToken | null> {
    if (this.isImpersonating()) {
      return of(null);
    }
    return this.http.post<AuthToken>(`${environment.apiUrl}/auth/impersonate`, { id })
    .pipe(
      tap({
        next: (user: AuthToken) => {
          this.setAuthenticatedUser(user);
          this.navigateToHomePage(user);
        },
        error: (error: HttpErrorResponse) => {
          console.error('Impersonation failed:', error);
        }
      })
    );
  }

  isImpersonating(): boolean {
    return this.userValue?.impersonatorId != null;
  }

  returnFromImpersonation(): Observable<AuthToken> {
    return this.http.post<AuthToken>(`${environment.apiUrl}/auth/unimpersonate`, {})
    .pipe(
      tap({
        next: (user: AuthToken) => {
          this.setAuthenticatedUser(user);
          this.navigateToHomePage(user);
          location.reload();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to unimpersonate from server:', err);
          this.logout();
        }
      })
    );
  }

  logout() {
    localStorage.removeItem(AuthenticationService.USER_STORAGE_KEY);
    sessionStorage.removeItem(AuthenticationService.USER_STORAGE_KEY);

    this.activeUserStorage = undefined;

    this.userSubject.next(undefined);
    this.router.navigate(['/login']);
  }

  requirePasswordChange() {
    this.router.navigate(['change-password']);
  }

  updatePassword(currentPassword: string, newPassword: string) {
    return this.http.put(`${environment.apiUrl}/users/${this.userValue?.userId}/change-password`, { currentPassword, newPassword })
  }

  changePassword(password: string) {
    return this.http.put(`${environment.apiUrl}/users/${this.userValue?.userId}`, { requirePasswordUpdate: false, password })
    .pipe(
      switchMap(() => this.refresh())
    )
  }

  recoverPassword(email: string) {
    return this.http.post(`${environment.apiUrl}/auth/forgot-password`, { email });
  }

  resetPassword(token: string, newPassword: string) {
    return this.http.post(`${environment.apiUrl}/auth/reset-password`, { token, newPassword });
  }

  private setAuthenticatedUser(user: AuthToken, storage?: Storage) {
    this.persistUser(user, storage);
    this.userSubject.next(user);
  }

  private navigateToHomePage(user: AuthToken) {
    this.router.navigateByUrl(user.homePage || '/');
  }

  private getStoredUser(): AuthToken | null {
    const storedLocalUser = this.readUserFromStorage(localStorage);
    if (storedLocalUser) {
      this.activeUserStorage = localStorage;
      return storedLocalUser;
    }

    const storedSessionUser = this.readUserFromStorage(sessionStorage);
    if (storedSessionUser) {
      this.activeUserStorage = sessionStorage;
      return storedSessionUser;
    }

    return null;
  }

  private readUserFromStorage(storage: Storage): AuthToken | null {
    const storedUser = storage.getItem(AuthenticationService.USER_STORAGE_KEY);
    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser) as AuthToken;
    } catch {
      storage.removeItem(AuthenticationService.USER_STORAGE_KEY);
      return null;
    }
  }

  private persistUser(user: AuthToken, storage: Storage = this.activeUserStorage ?? localStorage) {
    const otherStorage = storage === localStorage ? sessionStorage : localStorage;

    try {
      storage.setItem(AuthenticationService.USER_STORAGE_KEY, JSON.stringify(user));
      otherStorage.removeItem(AuthenticationService.USER_STORAGE_KEY);
      this.activeUserStorage = storage;
    } catch {
    }
  }
}
